/**
 * Round 119 (Phase F2) — Public Business OS Portal.
 *
 * Recipients land here from an emailed share link. NO auth required.
 * The route is `/portal/:token`. Backend verifies the token and
 * returns branded metadata; this page renders the recipient's
 * branded view + download button (if permission granted).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Package, Download, Clock, ShieldWarning } from '@phosphor-icons/react';
import { businessOsPortalAPI } from '@/utils/api';
import SnapshotDiffCard from '@/components/SnapshotDiffCard';
import CommentThread from '@/components/CommentThread';

const BusinessOSPortalPage = () => {
  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentSummary, setCommentSummary] = useState({});

  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${backend}/api/business-os/portal/${token}`);
        setPortal(r.data?.portal || null);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is invalid, expired, or revoked.');
      }
    })();
  }, [backend, token]);

  useEffect(() => {
    (async () => {
      try {
        const r = await businessOsPortalAPI.diff(token);
        setDiff(r?.diff || null);
      } catch { /* diff is optional */ }
      finally { setDiffLoading(false); }
    })();
  }, [token]);

  const refreshComments = useCallback(async () => {
    try {
      const r = await businessOsPortalAPI.listComments(token);
      setComments(r?.comments || []);
      setCommentSummary(r?.summary || {});
    } catch { /* comments optional */ }
  }, [token]);

  useEffect(() => { refreshComments(); }, [refreshComments]);

  const brand = portal?.brand_tokens || {};
  const styleVars = useMemo(() => ({
    '--p-primary': brand.primary || '#0F172A',
    '--p-primary-alt': brand.primary_alt || '#22C55E',
    '--p-heading': brand.heading_font ? `"${brand.heading_font}", system-ui, sans-serif` : 'system-ui, sans-serif',
    '--p-body': brand.body_font ? `"${brand.body_font}", system-ui, sans-serif` : 'system-ui, sans-serif',
  }), [brand]);

  const runDownload = async () => {
    setDownloading(true);
    try {
      // The portal download endpoint streams the ZIP directly (no JSON wrapper).
      window.open(`${backend}/api/business-os/portal/${token}/download`, '_blank');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download not permitted.');
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4" data-testid="portal-error">
        <div className="max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-8 text-center">
          <ShieldWarning size={40} className="mx-auto text-red-300 mb-3" />
          <div className="text-lg font-semibold text-red-100 mb-1">Access denied</div>
          <p className="text-red-200/90 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!portal) return <div className="min-h-screen bg-slate-950 text-slate-500 p-10">Loading…</div>;

  const canDownload = (portal.permissions || []).includes('download');
  const canComment = (portal.permissions || []).includes('comment')
    || (portal.permissions || []).includes('editor')
    || (portal.permissions || []).includes('admin');
  const canApprove = canComment; // approve/request_revision use comment perm

  const submitComment = async (payload) => {
    const r = await businessOsPortalAPI.createComment(token, {
      ...payload, target_type: 'snapshot',
    });
    if (r?.comment) setComments((prev) => [...prev, r.comment]);
    await refreshComments();
  };

  const resolveComment = async (commentId) => {
    try {
      await businessOsPortalAPI.resolveComment(token, commentId, { author_name: '' });
      await refreshComments();
    } catch {
      toast.error('Could not resolve.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" style={styleVars} data-testid="portal-page">
      <header
        className="text-white px-6 py-16"
        style={{ background: 'var(--p-primary)' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          {portal.primary_logo && (
            <img src={portal.primary_logo} alt="" className="mx-auto h-16 mb-6 object-contain" />
          )}
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'var(--p-heading)' }} data-testid="portal-business-name">
            {portal.business_name}
          </h1>
          {portal.tagline && (
            <p className="text-white/85 italic" style={{ fontFamily: 'var(--p-body)' }}>{portal.tagline}</p>
          )}
          {portal.recipient_name && (
            <p className="mt-6 text-white/90" style={{ fontFamily: 'var(--p-body)' }}>
              A private preview for <span className="font-semibold">{portal.recipient_name}</span>.
            </p>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="portal-main">
        {portal.message && (
          <section className="rounded-xl border border-slate-200 bg-white p-5 text-slate-700" style={{ fontFamily: 'var(--p-body)' }}>
            {portal.message}
          </section>
        )}

        <section data-testid="portal-diff-section">
          <SnapshotDiffCard
            diff={diff}
            loading={diffLoading}
            accentColor={brand.primary_alt || '#22C55E'}
            theme="light"
            testid="portal-diff"
          />
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <Package size={20} style={{ color: 'var(--p-primary)' }} />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--p-heading)' }}>
              What's included in this Business OS
            </h2>
          </div>
          {Object.keys(portal.grouped_assets || {}).length === 0 && (
            <p className="text-slate-500 text-sm">No approved assets yet.</p>
          )}
          <div className="space-y-4">
            {Object.entries(portal.grouped_assets || {}).map(([type, rows]) => (
              <div key={type} data-testid={`portal-group-${type}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  {type.replace(/_/g, ' ')} · {rows.length}
                </div>
                <ul className="space-y-1">
                  {rows.map((r) => (
                    <li key={r.id} className="text-sm text-slate-700">
                      • {r.title || r.id}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {canDownload && (
          <button
            onClick={runDownload}
            disabled={downloading}
            data-testid="portal-download"
            className="w-full rounded-xl py-4 text-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            style={{ background: 'var(--p-primary-alt)', fontFamily: 'var(--p-body)' }}
          >
            {downloading ? 'Preparing download…' : (
              <span className="inline-flex items-center gap-2">
                <Download size={18} /> Download Business OS v{portal.snapshot?.version}
              </span>
            )}
          </button>
        )}

        {portal.expires_at && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-3 text-xs flex items-center gap-2" data-testid="portal-expires">
            <Clock size={14} /> Access expires {new Date(portal.expires_at).toLocaleString()}
          </div>
        )}

        {(canComment || comments.length > 0) && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="portal-comments-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--p-heading)' }}>
                Feedback & Approvals
              </h2>
              {commentSummary?.approve > 0 && (
                <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                  {commentSummary.approve} approval{commentSummary.approve === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <CommentThread
              comments={comments}
              onSubmit={submitComment}
              onResolve={resolveComment}
              authorName={portal.recipient_name || ''}
              canComment={canComment}
              canApprove={canApprove}
              canResolve={canComment}
              accentColor={brand.primary_alt || '#22C55E'}
              testid="portal-comments"
            />
          </section>
        )}
      </main>
    </div>
  );
};

export default BusinessOSPortalPage;
